import { Link } from "@tanstack/react-router";
import { GRAMS_PER_TOLA } from "@/lib/gold-data";

/**
 * Transparency panel for live-rate pages.
 *
 * Everything stated here is derived from the actual pricing pipeline in
 * `src/routes/api/public/hooks/gold-rates.ts` — international spot in USD
 * per troy ounce, converted at the interbank USD rate, plus the local
 * market premium that Pakistani and Indian dealers add over spot. No
 * invented credentials, no invented review dates: only the mechanics a
 * reader can check against the numbers on the page.
 */

type Country = "pakistan" | "india";

const COPY: Record<
  Country,
  {
    currency: string;
    unit: string;
    unitGrams: number;
    formula: string;
    localNote: string;
    marketNote: string;
  }
> = {
  pakistan: {
    currency: "PKR",
    unit: "tola",
    unitGrams: GRAMS_PER_TOLA,
    formula:
      "24K per tola = (spot USD per troy ounce ÷ 2.6667) × USD/PKR + local premium",
    localNote:
      "One tola is 11.6638 grams, so a troy ounce (31.1035 g) contains about 2.6667 tola. That divisor is why a Pakistani tola price is never a round fraction of the international ounce price.",
    marketNote:
      "Pakistani sarafa associations quote a rate that sits above raw spot because it carries import cost, dealer margin and the open-market dollar rate rather than the interbank one. The premium applied here is a flat working figure, not a live feed from any single bazaar — so on a fast-moving day the shop counter in Karachi or Lahore can differ by a few hundred rupees.",
  },
  india: {
    currency: "INR",
    unit: "10 grams",
    unitGrams: 10,
    formula:
      "24K per 10 g = (spot USD per troy ounce ÷ 3.11035) × USD/INR + local premium",
    localNote:
      "India quotes in 10-gram units, and a troy ounce is 31.1035 grams — so the ounce divides into 3.11035 ten-gram units exactly.",
    marketNote:
      "Indian retail rates additionally carry import duty, GST and jeweller making charges, which are applied at the counter and are not part of the metal rate shown here. Treat this page as the metal value, and expect a jeweller's bill to be higher once making charges and tax are added.",
  },
};

export function RateMethodology({ country }: { country: Country }) {
  const c = COPY[country];
  return (
    <section
      className="mx-auto max-w-4xl px-4 py-10"
      aria-labelledby="how-this-rate-is-calculated"
    >
      <h2
        id="how-this-rate-is-calculated"
        className="text-xl md:text-2xl font-bold mb-3"
      >
        How this rate is calculated (and where it can be wrong)
      </h2>

      <div className="rounded-2xl border border-border bg-card p-4 md:p-5">
        <p className="text-sm md:text-base leading-relaxed text-foreground/85">
          The number at the top of this page is not typed in by hand and it is
          not scraped from another rate site. It is computed from two live
          inputs — the international gold spot price in US dollars per troy
          ounce, and the USD/{c.currency} exchange rate — and then converted
          into the unit {country === "pakistan" ? "Pakistan" : "India"} actually
          trades in.
        </p>

        <div className="mt-4 overflow-x-auto rounded-xl bg-muted/50 p-3">
          <code className="whitespace-nowrap text-xs md:text-sm font-mono">
            {c.formula}
          </code>
        </div>

        <dl className="mt-4 grid gap-3 sm:grid-cols-2 text-sm">
          <div>
            <dt className="font-semibold">Refresh cadence</dt>
            <dd className="text-foreground/80">
              Recalculated roughly every 10 minutes while international markets
              are open. Weekend and holiday values hold the last traded spot
              price, because the metal itself is not trading.
            </dd>
          </div>
          <div>
            <dt className="font-semibold">Unit conversion</dt>
            <dd className="text-foreground/80">{c.localNote}</dd>
          </div>
          <div>
            <dt className="font-semibold">Karat purity</dt>
            <dd className="text-foreground/80">
              22K is 22/24 = 91.67% pure, 21K is 87.5%, 18K is 75%. Each lower
              karat rate on this page is the 24K rate multiplied by that
              fraction — jewellery alloy is otherwise identical metal.
            </dd>
          </div>
          <div>
            <dt className="font-semibold">Known limitation</dt>
            <dd className="text-foreground/80">{c.marketNote}</dd>
          </div>
        </dl>

        <p className="mt-4 text-sm text-foreground/80">
          If a figure here looks wrong against your local sarafa board, tell us
          which city and what you were quoted —{" "}
          <Link to="/contact" className="font-medium underline">
            report a rate discrepancy
          </Link>
          . Corrections to the premium are applied to the pricing pipeline, not
          patched page by page. Full workings, worked examples and our
          correction policy are on the{" "}
          <Link to="/methodology" className="font-medium underline">
            pricing methodology page
          </Link>
          .
        </p>

        <p className="mt-3 text-xs text-muted-foreground">
          Reference weight used on this page: 1 {c.unit} ={" "}
          {c.unitGrams.toFixed(4).replace(/0+$/, "").replace(/\.$/, "")} grams.
          Gold is a volatile asset; nothing here is investment advice.
        </p>
      </div>
    </section>
  );
}
